/**
 * Flight Expenses mode switcher — Dashboard / List.
 *
 * MAJOR module per PATTERNS.md "Module weight": two GENUINE listing views, not
 * one view with a decorative chip. The test that pattern sets is *"does an
 * operator ever open this module to read the state of the register rather than
 * to find one row?"* — and on an approvals-bound expense register the answer is
 * yes: what is unpriced, what has no payer recorded, and which flights have
 * claims waiting is exactly why the office opens it. Aaron made that call
 * (2026-08-30); it is not inferred from module size.
 *
 * TWO MODES ONLY, and deliberately not more. The obvious third and fourth would
 * be status modes ("Submitted" / "Approved"), and there is nothing to build
 * them from: an expense row has no status of its own. `flight_landings.status_id`
 * exists but is unused by every kind on the table (it is 0 on all 84 rows of
 * the reference dump), and approval state lives on the `authorised_requests`
 * row, which is reachable only through the polymorphic (source, source_id)
 * pair with no join declarable across it. Inventing those modes would be the
 * decorative-chip anti-pattern with a route attached.
 *
 * Mirrored slavishly from src/cycle-count/CycleCountLayout.tsx, which mirrors
 * FleetLayout → ComponentLayout, the canonical instance. Same
 * Title-wraps-BreadcrumbHoverRoot structure, same allRoutes/breadcrumbMenu
 * split so the parent crumb stays a plain link.
 *
 * The base path is DERIVED from the live pathname (BlankLayout pattern), never
 * hardcoded — this directory is `flight-expenses` while the nav route is
 * `flight_expenses`, and a hardcoded "/flight-expenses/..." breadcrumb map
 * silently misses on the real pathname: every lookup returns undefined and the
 * header renders a bare "/ ⌄" with no title.
 */
import { useMemo } from "react";

import { Box, Button, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Outlet, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
  describeMode,
} from "../components/BreadcrumbDropdownMenu";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import { ListingRouteBaseProvider } from "../components/datagrid/ListingRouteBase";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";

import type { AeronetViewProps } from "../types";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/** Route key → label. Hardcoded rather than config-resolved on purpose: unlike
 *  Cycle Count's statuses these are not a tenant vocabulary, they are this
 *  module's two views. There is no `flight_expenses_*` config family on any
 *  tenant (the only related row is `flight_loc_expense_type_id`, which is
 *  EMPTY on module_g2_demo — measured 2026-08-30). */
export const MODE_LABELS: Record<string, string> = {
  // "Dashboard", not "Overview" (Aaron 2026-09-28, H94: no "overviews" at a
  // module level). `<base>/overview` redirects here (index.tsx).
  dashboard: "Dashboard",
  // "List", not "All expenses" (Aaron, 2026-08-30: "why does it not say list
  // like other modules"). The route segment was already `list`; only the LABEL
  // deviated, which is the same mint-your-own-vocabulary defect the port
  // checklist calls out for segments — it applies to the label too, and was
  // not applied here. Opportunities (OpportunityLayout.tsx:42) is the shipped
  // precedent for the plain "List".
  list: "List",
  // OPS Patch 1.4: km and per diem rates (FlightExpenseRates).
  rates: "Rates",
  // OPS Patch 5 L2 (Pierre's 32): suppliers typed on expenses, for accounting
  // (FlightExpenseSuppliers).
  suppliers: "Suppliers to confirm",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  dashboard: "Claims needing attention",
  list: "Every expense claim",
  rates: "Kilometre and meal rates",
  suppliers: "New suppliers for accounting",
};

const FlightExpensesLayout: React.FC<AeronetViewProps> = ({ title }) => {
  const location = useLocation();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "flightExpensesBreadcrumbMenu",
  });

  const breadcrumbMenu = useMemo(() => MODE_LABELS, []);

  const segments = location.pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = Object.prototype.hasOwnProperty.call(
    breadcrumbMenu,
    lastSegment
  );
  const currentMode = onMode ? lastSegment : "dashboard";
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  const basePath = baseSegments.length ? "/" + baseSegments.join("/") : "";

  return (
    <Box height="100%">
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          <LinkRouter to={basePath || "/"}>
            {title || "Flight Expenses"} /
          </LinkRouter>
          <BreadcrumbHoverRoot popupState={popupState}>
            <Button
              endIcon={<BreadcrumbChevronIcon />}
              sx={breadcrumbCurrentTriggerSx}
            >
              <Typography
                variant="h2"
                component="span"
                sx={breadcrumbCurrentLabelSx}
              >
                {breadcrumbMenu[currentMode]}
              </Typography>
            </Button>
            <BreadcrumbDropdownMenu popupState={popupState}>
              {Object.entries(breadcrumbMenu).map(([slug, label]) => (
                <BreadcrumbMenuLinkItem
                  key={slug}
                  to={`${basePath}/${slug}`}
                  label={label}
                  description={describeMode(MODE_DESCRIPTIONS, slug)}
                  selected={currentMode === slug}
                  onClick={popupState.close}
                />
              ))}
            </BreadcrumbDropdownMenu>
          </BreadcrumbHoverRoot>
        </Title>
      </BreadcrumbHeaderListing>
      {/* Makes the shared DataGrid's row-click / create resolve against the
          module base rather than the current mode segment — the record routes
          are SIBLINGS of the modes, not children. */}
      <ListingRouteBaseProvider value={{ base: basePath }}>
        <Outlet />
      </ListingRouteBaseProvider>
    </Box>
  );
};

export default FlightExpensesLayout;
